// components/KeywordsModal.js
import React, { useState, useEffect, useRef } from 'react';
import { X, Tag, User, MapPin, Building, Calendar, Hash, Plus, Search } from 'lucide-react';
import styles from './KeywordsModal.module.css';
import { getKeywordsFromAI } from '../api/aiKeywordsApi';
import { extractKeywordsWithTextRank } from '../utils/textRank';
import { extractKeywordsCombined } from '../utils/combinedKeywordExtractor';
import { useNotes } from './contexts/NotesContext';

const cleanTags = (rawTags) => {
  if (!Array.isArray(rawTags) || rawTags.length === 0) {
    return { cleanedTags: [], machine: 't:', human: '' };
  }

  // Step 1: Normalize
  const normalized = rawTags.map(tag => {
    return tag
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/\s/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/(\w)s$/, '$1')  // Simple singularization
      .replace(/(\w)ies$/, '$1y'); // features -> feature, companies -> company
  }).filter(tag => tag.length > 0);

  // Step 2: Drop pronoun/determiner phrases
  const pronouns = new Set(['i', 'me', 'my', 'we', 'our', 'you', 'your', 'he', 'she', 'they', 'it', 'its', 'this', 'that', 'these', 'those']);
  const withoutPronouns = normalized.filter(tag => {
    const tokens = tag.split('-');
    return !tokens.some(token => pronouns.has(token));
  });

  // Step 3: Acronym collapse (local)
  const tokenCounts = {};
  withoutPronouns.forEach(tag => {
    const tokens = tag.split('-');
    tokens.forEach(token => {
      if (token.length >= 2 && token.length <= 5 && /^[a-z]+$/.test(token)) {
        tokenCounts[token] = (tokenCounts[token] || 0) + 1;
      }
    });
  });
  
  const acronyms = Object.keys(tokenCounts).filter(token => tokenCounts[token] >= 2);
  const acronymCollapsed = withoutPronouns.map(tag => {
    const tokens = tag.split('-');
    const foundAcronym = tokens.find(token => acronyms.includes(token));
    return foundAcronym || tag;
  });

  // Step 4: Prefer phrases; hide singles (strict mode)
  const multiWordOnly = acronymCollapsed;   

  // Step 5: Skip cross-sentence mashup detection (would need original text)

  // Step 6: De-duplicate
  const unique = [...new Set(multiWordOnly)];

  // Step 7: Cap & order (keep up to 8)
  const cleanedTags = unique.slice(0, 8);

  // Generate output strings
  const machine = `t:${cleanedTags.join(',')}`;
  const human = cleanedTags.join(' • ');

  return { cleanedTags, machine, human };
};

const KeywordsModal = ({ onClose, editor, selectedNoteId }) => {
  const { notes, subnotes, updateNoteTags } = useNotes();
  const [keywords, setKeywords] = useState([]);
  const [entities, setEntities] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionMethod, setExtractionMethod] = useState(null);
  const [mainTopic, setMainTopic] = useState(null);
  const [executionTime, setExecutionTime] = useState(null);
  const [wordCount, setWordCount] = useState(null);
  const [documentWordCount, setDocumentWordCount] = useState(0);
  const [newTag, setNewTag] = useState('');
  const newTagInputRef = useRef(null);
  
  // Get plain text from HTML content
  const getPlainText = (html) => {
    return html.replace(/<[^>]*>/g, ' ').trim();
  };
  
  // Remove a keyword from the list
  const removeKeyword = (index) => {
    setKeywords(prev => prev.filter((_, i) => i !== index));
  };
  
  // Add a new user-defined tag
  const addNewTag = () => {
    if (newTag.trim().length > 0) {
      // Check if keyword already exists (exact match or case insensitive)
      const tagExists = keywords.some(
        keyword => keyword === newTag.trim() || keyword.toLowerCase() === newTag.trim().toLowerCase()
      );
      
      if (!tagExists) {
        setKeywords(prev => [...prev, newTag.trim()]);
      }
      setNewTag('');
      if (newTagInputRef.current) {
        newTagInputRef.current.focus();
      }
    }
  };
  
  // Handle key press in the new tag input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      addNewTag();
    }
  };
  
  // Load existing tags from the note
// Load existing tags from the note
useEffect(() => {
  if (selectedNoteId) {
    // First look in main notes array
    let currentNote = notes.find(note => note.id === selectedNoteId);
    
    // If not found in main notes, check in subnotes
    if (!currentNote) {
      for (const parentId in subnotes) {
        const foundNote = subnotes[parentId]?.find(note => note.id === selectedNoteId);
        if (foundNote) {
          currentNote = foundNote;
          break;
        }
      }
    }
    
    if (currentNote && currentNote.tags) {
      try {
        const tagsList = JSON.parse(currentNote.tags);
        if (Array.isArray(tagsList)) {
          setKeywords(tagsList);
        } else if (typeof currentNote.tags === 'string' && currentNote.tags.trim() !== '') {
          // Legacy format or comma-separated string
          setKeywords(currentNote.tags.split(',').map(tag => tag.trim()).filter(tag => tag));
        }
      } catch (e) {
        // If JSON parsing fails, try comma-separated format
        if (typeof currentNote.tags === 'string' && currentNote.tags.trim() !== '') {
          setKeywords(currentNote.tags.split(',').map(tag => tag.trim()).filter(tag => tag));
        }
      }
    }
  }
}, [selectedNoteId, notes, subnotes]); // Add subnotes to dependencies

// Save keywords to the note
const saveKeywords = async () => {
  console.log("saveKeywords function called");
  
  if (!selectedNoteId) {
    console.log("No selectedNoteId found");
    return;
  }
  
  // First check in main notes array
  let currentNote = notes.find(note => note.id === selectedNoteId);
  
  // If not found in main notes, check in subnotes
  if (!currentNote) {
    console.log("Note not found in main notes array, checking subnotes...");
    for (const parentId in subnotes) {
      const foundNote = subnotes[parentId]?.find(note => note.id === selectedNoteId);
      if (foundNote) {
        currentNote = foundNote;
        console.log(`Found note in subnotes with parent ID: ${parentId}`);
        break;
      }
    }
  }
  
  if (!currentNote) {
    console.log("Note not found in either main notes or subnotes");
    return;
  }
  
  try {
    console.log("Attempting to save keywords:", keywords);
    
    // Remove duplicate keywords
    const uniqueKeywords = [];
    keywords.forEach(keyword => {
      if (!uniqueKeywords.includes(keyword)) {
        uniqueKeywords.push(keyword);
      }
    });
    
    console.log("uniqueKeywords:", uniqueKeywords);
    
    // Format keywords as JSON string
    const tagsString = JSON.stringify(uniqueKeywords);
    console.log("tagsString:", tagsString);
    
    // Create updated note object
    const updatedNote = {
      ...currentNote,
      tags: tagsString
    };
    console.log("updatedNote:", updatedNote);
    
    // Check if updateNoteTags exists
    console.log("updateNoteTags exists:", typeof updateNoteTags === 'function');
    
    if (typeof updateNoteTags === 'function') {
      // Try using updateNoteTags
      await updateNoteTags(selectedNoteId, uniqueKeywords);
      console.log("updateNoteTags call completed");
    } else {
      // Fall back to using updateNote directly
      console.log("Falling back to updateNote");
      await updateNote(selectedNoteId, updatedNote);
      console.log("updateNote call completed");
    }
    
    console.log("About to close modal");
    onClose();
  } catch (err) {
    console.error('Error in saveKeywords:', err);
    alert('Failed to save keywords');
  }
};
  
  // Handles AI extraction
  async function handleAIExtraction() {
    if (!editor || !selectedNoteId) return;
    
    const noteContent = editor.root.innerHTML;
    if (!noteContent.trim()) {
      alert('Note is empty');
      return;
    }
    
    setIsExtracting(true);
    setExtractionMethod('AI');
    
    try {
      const extracted = await getKeywordsFromAI(noteContent);
      if (extracted && extracted.pairs && extracted.pairs.length > 0) {
        // Format as simple keywords for tag display
        const formattedKeywords = extracted.pairs.map(pair => pair[0]);
        
        // Merge with existing keywords, avoiding duplicates (exact match or case insensitive)
        const newKeywords = [...keywords];
        formattedKeywords.forEach(keyword => {
          const exists = newKeywords.some(
            k => k === keyword || k.toLowerCase() === keyword.toLowerCase()
          );
          if (!exists) {
            newKeywords.push(keyword);
          }
        });
        
        setKeywords(newKeywords);
        setEntities([]);
        setMainTopic(null);
      } else {
        alert('No keywords could be extracted from the note');
      }
    } catch (err) {
      console.error('Error extracting keywords:', err);
      alert('Failed to extract keywords');
    } finally {
      setIsExtracting(false);
    }
  }
  
  // Handles TextRank extraction
  function handleTextRankExtraction() {
    if (!editor || !selectedNoteId) return;
    
    const noteContent = editor.root.innerHTML;
    if (!noteContent.trim()) {
      alert('Note is empty');
      return;
    }
    
    setIsExtracting(true);
    setExtractionMethod('TextRank');
    
    try {
      const startTime = performance.now();
      
      // Get plain text from HTML content
      const plainText = getPlainText(noteContent);
      
      // Extract keywords using TextRank
      const extracted = extractKeywordsWithTextRank(plainText);
      
      const endTime = performance.now();
      setExecutionTime(endTime - startTime);
      setWordCount(plainText.trim().split(/\s+/).length);
      
      if (extracted && extracted.length > 0) {
  // Merge with existing keywords, avoiding duplicates
  const newKeywords = [...keywords];
  extracted.forEach(keyword => {
    const exists = newKeywords.some(
      k => k === keyword || k.toLowerCase() === keyword.toLowerCase()
    );
    if (!exists) {
      newKeywords.push(keyword);
    }
  });
  
  setKeywords(newKeywords);
  
  // Clean and log the tags
  const cleaned = cleanTags(newKeywords);
  console.log('TextRank Extraction - Cleaned tags:', cleaned);
  
  setEntities([]);
  setMainTopic(null);

      } else {
        alert('No keywords could be extracted from the note');
      }
    } catch (err) {
      console.error('Error extracting keywords with TextRank:', err);
      alert('Failed to extract keywords with TextRank');
    } finally {
      setIsExtracting(false);
    }
  }
  
  // Handles Combined extraction
  function handleCombinedExtraction() {
    if (!editor || !selectedNoteId) return;
    
    const noteContent = editor.root.innerHTML;
    if (!noteContent.trim()) {
      alert('Note is empty');
      return;
    }
    
    setIsExtracting(true);
    setExtractionMethod('Combined');
    
    try {
      const startTime = performance.now();
      
      // Get plain text from HTML content
      const plainText = getPlainText(noteContent);
      
      // Extract with combined approach
      const result = extractKeywordsCombined(plainText);
      
      const endTime = performance.now();
      setExecutionTime(result.executionTimeMs || (endTime - startTime));
      setWordCount(result.wordCount);
      
      if (result && result.keywords && result.keywords.length > 0) {
  // Extract just the keyword text for tag display
  const keywordTexts = result.keywords.map(keyword => keyword.text);
  
  // Merge with existing keywords, avoiding duplicates
  const newKeywords = [...keywords];
  keywordTexts.forEach(keyword => {
    const exists = newKeywords.some(
      k => k === keyword || k.toLowerCase() === keyword.toLowerCase()
    );
    if (!exists) {
      newKeywords.push(keyword);
    }
  });
  
  setKeywords(newKeywords);
  
  // Clean and log the tags
  //const cleaned = cleanTags(newKeywords);
 // console.log('Combined Extraction - Cleaned tags:', cleaned);
  
  setEntities(result.entities || []);
  setMainTopic(result.mainTopic);

      } else {
        alert('No keywords could be extracted from the note');
      }
    } catch (err) {
      console.error('Error with combined extraction:', err);
      alert('Failed to extract with combined method');
    } finally {
      setIsExtracting(false);
    }
  }

  // Calculate document word count on mount
  useEffect(() => {
    if (editor) {
      const plainText = getPlainText(editor.root.innerHTML);
      const count = plainText.trim().split(/\s+/).length;
      setDocumentWordCount(count);
    }
  }, [editor]);
  
  // Render entity icon based on type
  const renderEntityIcon = (type) => {
    switch (type) {
      case 'person':
        return <User size={16} />;
      case 'place':
        return <MapPin size={16} />;
      case 'organization':
        return <Building size={16} />;
      case 'date':
        return <Calendar size={16} />;
      default:
        return <Hash size={16} />;
    }
  };
  
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h3>
            Keywords & Tags
            {extractionMethod && ` (${extractionMethod})`}
            {executionTime && ` • ${executionTime.toFixed(1)}ms`}
          </h3>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <div className={styles.modalContent}>
          {/* Main topic if available */}
          {mainTopic && (
            <div className={styles.mainTopic} style={{ 
              marginBottom: '16px', 
              padding: '8px', 
              background: '#f0f7ff', 
              borderRadius: '4px' 
            }}>
              <h4 style={{ margin: '0 0 4px 0' }}>Main Topic</h4>
              <p style={{ margin: 0 }}>{mainTopic}</p>
            </div>
          )}
          
          {/* Keywords as tags with ability to add custom tags */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '12px' 
            }}>
              <h4 style={{ margin: '0 12px 0 0' }}>Keywords</h4>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                border: '1px solid #ddd', 
                borderRadius: '16px', 
                padding: '2px 8px', 
                flexGrow: 1 
              }}>
                <input
                  ref={newTagInputRef}
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Add a custom tag..."
                  style={{ 
                    border: 'none', 
                    outline: 'none', 
                    padding: '6px', 
                    width: '100%',
                    fontSize: '14px' 
                  }}
                />
                <button
                  onClick={addNewTag}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: newTag.trim() ? '#e0e0ff' : '#f0f0f0',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    cursor: newTag.trim() ? 'pointer' : 'default',
                    opacity: newTag.trim() ? 1 : 0.5
                  }}
                  disabled={!newTag.trim()}
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
            
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '8px',
              minHeight: '40px',
              padding: '8px',
              border: keywords.length === 0 ? '1px dashed #ddd' : 'none',
              borderRadius: '4px'
            }}>
              {keywords.length === 0 ? (
                <span style={{ color: '#888', fontSize: '14px' }}>
                  No keywords yet. Add some or use extraction methods below.
                </span>
              ) : (
                keywords.map((keyword, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#e9f5fe',
                      padding: '4px 8px',
                      borderRadius: '16px',
                      fontSize: '14px'
                    }}
                  >
                    <Tag size={14} style={{ marginRight: '4px' }} />
                    <span>{keyword}</span>
                    <button
                      onClick={() => removeKeyword(index)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: '4px',
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        padding: '2px',
                        borderRadius: '50%'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* Extraction buttons */}
          <div style={{ marginTop: '24px' }}>
            <h4 style={{ margin: '0 0 12px 0' }}>Extract Keywords</h4>
            
            {documentWordCount > 0 && (
              <p style={{ 
                fontSize: '14px', 
                color: documentWordCount > 1000 ? '#e57373' : '#666',
                marginTop: '4px',
                marginBottom: '16px'
              }}>
                Document size: <strong>{documentWordCount} words</strong>
                {documentWordCount > 1000 && ' (TextRank recommended for longer documents)'}
              </p>
            )}
            
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '10px',
              marginBottom: '20px' 
            }}>
              <button
                onClick={handleCombinedExtraction}
                disabled={isExtracting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #2196f3',
                  background: '#e3f2fd',
                  color: '#0d47a1',
                  cursor: isExtracting ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                <Search size={16} style={{ marginRight: '6px' }} />
                Combined Extraction
              </button>
              
              <button
                onClick={handleTextRankExtraction}
                disabled={isExtracting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #4caf50',
                  background: '#e8f5e9',
                  color: '#1b5e20',
                  cursor: isExtracting ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                <Tag size={16} style={{ marginRight: '6px' }} />
                TextRank Extraction
              </button>
              
              <button
                onClick={handleAIExtraction}
                disabled={isExtracting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #9c27b0',
                  background: '#f3e5f5',
                  color: '#4a148c',
                  cursor: isExtracting ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                <Search size={16} style={{ marginRight: '6px' }} />
                AI-Powered Extraction
              </button>
            </div>
            
            {isExtracting && (
              <div style={{ 
                textAlign: 'center', 
                padding: '16px', 
                color: '#666',
                fontSize: '14px'
              }}>
                <div className={styles.spinner}></div>
                <p>Extracting keywords...</p>
              </div>
            )}
          </div>
          
          {/* Entities if available */}
          {entities && entities.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ margin: '16px 0 8px 0' }}>Named Entities</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {entities.map((entity, index) => (
                  <div 
                    key={index} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '4px 8px', 
                      background: '#f5f5f5',
                      borderRadius: '16px',
                      fontSize: '14px'
                    }}
                  >
                    {renderEntityIcon(entity.type)}
                    <span style={{ marginLeft: '4px' }}>{entity.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className={styles.modalFooter}>
          <button 
            onClick={onClose}
            style={{ marginRight: '8px' }}
            className={styles.cancelButton}
          >
            Cancel
          </button>
          <button 
  onClick={() => {
    console.log("Save keywords button clicked");
    saveKeywords();
  }}
  className={styles.saveButton} 
  disabled={isExtracting}
>
  Save Keywords
</button>
        </div>
      </div>
    </div>
  );
};

export default KeywordsModal;